import {
  ORG_LOGO_CONTENT_TYPES,
  ORG_LOGO_MAX_BYTES,
  type AdminOrgDto,
  type OrgLogoContentType,
} from '@carcheck/shared';

import { companyErrorKey } from './companyErrors';
import { confirmOrgLogo, requestOrgLogoUpload } from '../../../api/admin-orgs';

/**
 * Company logo upload (AUT-403 / CP-10b), three hops:
 *
 *   1. `POST /admin/orgs/:id/logo/upload-url {contentType}` → presigned PUT
 *   2. `PUT <uploadUrl>` — plain `fetch`, NEVER `apiClient`: the axios
 *      instance injects `Authorization: Bearer …`, and S3 / R2 refuses a
 *      presigned URL that carries a second auth scheme. The only header is
 *      `Content-Type`, byte-equal to the one the server signed.
 *   3. `POST /admin/orgs/:id/logo/confirm {key}` — the server checks size and
 *      magic bytes (S3 does not sign Content-Length) and answers the company.
 *
 * Type and size are pre-checked here so an obvious mistake never costs a
 * presign. The browser PUT needs bucket CORS for the admin origin
 * (`infra/TWO_ENV_DEPLOY.md`); local MinIO answers `*`.
 */
export type LogoUploadFailure = 'type' | 'size' | 'put';

export class LogoUploadError extends Error {
  constructor(readonly reason: LogoUploadFailure) {
    super(`logo upload failed: ${reason}`);
    this.name = 'LogoUploadError';
  }
}

const isLogoType = (type: string): type is OrgLogoContentType =>
  (ORG_LOGO_CONTENT_TYPES as readonly string[]).includes(type);

/** `null` → acceptable; else the reason the file is refused before upload. */
export const precheckLogo = (file: File): LogoUploadFailure | null => {
  if (!isLogoType(file.type)) return 'type';
  if (file.size > ORG_LOGO_MAX_BYTES) return 'size';
  return null;
};

export const uploadOrgLogo = async (orgId: string, file: File): Promise<AdminOrgDto> => {
  const contentType = file.type;
  if (!isLogoType(contentType)) throw new LogoUploadError('type');
  if (file.size > ORG_LOGO_MAX_BYTES) throw new LogoUploadError('size');

  const slot = await requestOrgLogoUpload(orgId, contentType);
  let response: Response;
  try {
    response = await fetch(slot.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': slot.contentType },
      body: file,
    });
  } catch {
    throw new LogoUploadError('put');
  }
  if (!response.ok) throw new LogoUploadError('put');
  return confirmOrgLogo(orgId, slot.key);
};

/** i18n key for any failure of {@link uploadOrgLogo}. */
export const logoErrorKey = (err: unknown): string => {
  if (err instanceof LogoUploadError) return `companies.logo.errors.${err.reason}`;
  return companyErrorKey(err);
};
